import {
  buildDirectiveEnvelope,
  type DirectiveEnvelopePart,
} from "duck/ui/chat/directiveEnvelope";
import {
  $getRoot,
  $isElementNode,
  $isLineBreakNode,
  $isTextNode,
  type LexicalEditor,
  type LexicalNode,
} from "lexical";

import type { Directive } from "shared/api/directives/api";

import { getFiltersQuery } from "features/ui/Filters/FilterBuilder/utils";

import type { DirectiveComposerSuggestion } from "./DirectiveComposer.types";
import { $isDirectiveInvocationNode } from "./DirectiveComposerInvocationNode";

export const AVAILABLE_IN_FRONTEND_FILTER = getFiltersQuery(undefined, [
  { name: "availableInFrontend", op: "eq", value: true },
]);

export const getDirectiveComposerSuggestions = (
  items: Directive[]
): DirectiveComposerSuggestion[] =>
  items.flatMap((directive) =>
    directive.directiveText ? [{ name: directive.slashInvocationName }] : []
  );

const getNodeSubmitParts = (node: LexicalNode): DirectiveEnvelopePart[] => {
  if ($isDirectiveInvocationNode(node)) {
    return [{ type: "directive", name: node.getPayload().name }];
  }

  if ($isTextNode(node)) {
    return [{ type: "text", text: node.getTextContent() }];
  }

  if ($isLineBreakNode(node)) return [{ type: "text", text: "\n" }];

  if ($isElementNode(node)) {
    return node.getChildren().flatMap(getNodeSubmitParts);
  }

  return [];
};

// Walks the composer tree rather than reading root.getTextContent() directly,
// so each directive pill becomes an unexpanded reference the aiserver expands
// and persists, instead of its display form ("/name") or its instruction text.
export const getDirectiveComposerSubmitText = (
  editor: LexicalEditor | null
): string => {
  if (!editor) return "";

  return editor.getEditorState().read(() => {
    const newline: DirectiveEnvelopePart = { type: "text", text: "\n" };
    const parts = $getRoot()
      .getChildren()
      .flatMap((node, index) =>
        index === 0
          ? getNodeSubmitParts(node)
          : [newline, ...getNodeSubmitParts(node)]
      );

    return buildDirectiveEnvelope(parts);
  });
};
